/** Tipo de recurso que a api associa a uma sessão da programação. */
export const PROGRAMMING_RESOURCE_TYPE = 'Programming'

/**
 * Chaves i18n dos erros de check-in.
 *
 * Cada mensagem diz o que aconteceu **e** como resolver, conforme
 * `ACCESSIBILITY.md §5` — o texto anterior só dizia o que aconteceu.
 */
export const CHECK_IN_ERROR_KEYS = {
  mismatch: 'schedule.checkIn.errors.mismatch',
  unknown: 'schedule.checkIn.errors.unknown',
  network: 'schedule.checkIn.errors.network'
} as const

export type CheckInErrorKey =
  (typeof CHECK_IN_ERROR_KEYS)[keyof typeof CHECK_IN_ERROR_KEYS]

/**
 * Códigos que a api emite como `ApiErrors::FlowError` na conclusão da sessão.
 *
 * Os dois chegam como `422`: quem separa "palavra de outra sessão" de "palavra
 * que não existe no evento" é o `code`, nunca o status. A palavra certa é
 * decidida no servidor — o cliente não conhece nenhuma palavra para comparar.
 */
const KEYWORD_ERROR_CODES: Record<string, CheckInErrorKey> = {
  'programming.keyword.mismatch': CHECK_IN_ERROR_KEYS.mismatch,
  'programming.keyword.unknown': CHECK_IN_ERROR_KEYS.unknown
}

type FlowErrorEnvelope = {
  response?: {
    data?: {
      errors?: Array<{ code?: string | null }> | null
    } | null
  } | null
}

/**
 * Chave i18n do erro, lida do corpo da resposta.
 *
 * O envelope do `FlowError` é uma **lista** — `{ errors: [{ code, pointer,
 * status }] }` —, diferente do envelope de validação de model que o produtor
 * consome, que é um objeto indexado por campo. Corpo que não seja a lista cai
 * na mensagem genérica em vez de virar erro de palavra por engano.
 */
export const checkInErrorKeyFor = (error: unknown): CheckInErrorKey => {
  const errors = (error as FlowErrorEnvelope)?.response?.data?.errors

  if (!Array.isArray(errors)) return CHECK_IN_ERROR_KEYS.network

  const code = errors[0]?.code

  return (code && KEYWORD_ERROR_CODES[code]) || CHECK_IN_ERROR_KEYS.network
}
